import { useMemo } from "react";
import { cn } from "../lib/utils";
import { KpiCard } from "../components/ui/KpiCard";
import { StatusChip } from "../components/ui/StatusChip";
import { OwnerTag } from "../components/ui/OwnerTag";
import { PriorityDot } from "../components/ui/PriorityDot";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02Icon } from "@hugeicons/core-free-icons";
import {
  IconUsers,
  IconCircleCheck,
  IconMoneyBill,
  IconSwap,
  IconTriangleWarning,
  IconTasks,
  IconSquareKanban,
} from "nucleo-glass";
import { NucleoIcon } from "../components/ui/NucleoIcon";
import type { Card, ChecklistItem, LaneId } from "../data/types";
import type { RouteId } from "../lib/routes";
import type { KpiTotals, StatusCounts, ChecklistProgress } from "../lib/boardUtils";
import { getTaskMeta } from "../lib/taskMeta";

interface DashboardPageProps {
  cards: Card[];
  kpiTotals: KpiTotals;
  statusCounts: StatusCounts;
  checklistItems: ChecklistItem[];
  checklistProgress: ChecklistProgress;
  onToggleChecklist: (id: string) => void;
  onSelectCard: (cardId: string) => void;
  onNavigate?: (route: RouteId) => void;
}

const laneLabels: Record<LaneId, string> = {
  objective_kpis: "Objectives & KPIs",
  offer_pricing: "Offer & Pricing",
  channels: "Channels",
  commercial_pipeline: "Commercial Pipeline",
  weekly_content: "Weekly Content",
  revops_tracking: "RevOps & Tracking",
  feedback_loop: "Feedback Loop",
};

const LANE_ORDER: LaneId[] = [
  "objective_kpis", "offer_pricing", "channels",
  "commercial_pipeline", "weekly_content", "revops_tracking", "feedback_loop",
];

export function DashboardPage({
  cards,
  kpiTotals,
  statusCounts,
  checklistItems,
  checklistProgress,
  onToggleChecklist,
  onSelectCard,
  onNavigate,
}: DashboardPageProps) {
  // Cards that need attention: critical + (in_progress or blocked), sorted by priority
  const attentionCards = useMemo(() => {
    return cards
      .filter(
        (c) =>
          (c.priority === "critical" || c.priority === "high") &&
          (c.status === "in_progress" || c.status === "blocked"),
      )
      .slice(0, 6);
  }, [cards]);

  // Lane breakdown with task type counts
  const laneBreakdown = useMemo(() => {
    return LANE_ORDER.map((laneId) => {
      const laneCards = cards.filter((c) => c.laneId === laneId);
      const oneOff = laneCards.filter((c) => getTaskMeta(c).taskType === "one_off");
      const daily = laneCards.filter((c) => getTaskMeta(c).taskType === "recurring_daily");
      const weekly = laneCards.filter((c) => getTaskMeta(c).taskType === "recurring_weekly");
      return {
        id: laneId,
        label: laneLabels[laneId],
        oneOffCount: oneOff.length,
        dailyCount: daily.length,
        weeklyCount: weekly.length,
        planned: oneOff.filter((c) => c.status === "planned").length,
        active: oneOff.filter((c) => c.status === "in_progress").length,
        blocked: oneOff.filter((c) => c.status === "blocked").length,
        done: oneOff.filter((c) => c.status === "done").length,
      };
    });
  }, [cards]);

  const sections: Array<{ key: ChecklistItem["section"]; label: string }> = [
    { key: "joel", label: "Joel — Sales" },
    { key: "albert", label: "Albert — Product" },
    { key: "joint", label: "Joint — EOD" },
  ];

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full px-[var(--spacing-4)] py-[var(--spacing-6)] space-y-[var(--spacing-6)]">

        {/* KPI cards row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[var(--spacing-3)]">
          <div className="animate-stagger-in" style={{ animationDelay: "0ms" }}>
            <KpiCard
              label="Demos Booked"
              value={String(kpiTotals.demosBooked)}
              target="30"
              progress={(kpiTotals.demosBooked / 30) * 100}
              accentColor="var(--color-accent-300)"
              icon={<NucleoIcon icon={<IconUsers size={16} />} />}
            />
          </div>
          <div className="animate-stagger-in" style={{ animationDelay: "50ms" }}>
            <KpiCard
              label="Closed Won"
              value={String(kpiTotals.closedWon)}
              target="12"
              progress={(kpiTotals.closedWon / 12) * 100}
              accentColor={kpiTotals.closedWon >= 12 ? "var(--color-state-success)" : undefined}
              icon={<NucleoIcon icon={<IconCircleCheck size={16} />} />}
            />
          </div>
          <div className="animate-stagger-in" style={{ animationDelay: "100ms" }}>
            <KpiCard
              label="Revenue"
              value={`${(kpiTotals.newMrr / 1000).toFixed(1)}k€`}
              target="5.0k€"
              progress={(kpiTotals.newMrr / 5000) * 100}
              icon={<NucleoIcon icon={<IconMoneyBill size={16} />} />}
            />
          </div>
          <div className="animate-stagger-in" style={{ animationDelay: "150ms" }}>
            <KpiCard
              label="Trial → Paid"
              value={`${kpiTotals.trialToPaid}%`}
              target="20%"
              progress={(kpiTotals.trialToPaid / 20) * 100}
              accentColor={kpiTotals.trialToPaid >= 20 ? "var(--color-state-success)" : "var(--color-state-warning)"}
              icon={<NucleoIcon icon={<IconSwap size={16} />} />}
            />
          </div>
        </div>

        {/* Two-column: Attention + Checklist */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--spacing-3)]">
          {/* Needs attention */}
          <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] animate-stagger-in" style={{ animationDelay: "200ms" }}>
            <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-2)]">
              <NucleoIcon icon={<IconTriangleWarning size={16} />} className="text-[var(--color-state-warning)]" />
              <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Needs Attention</span>
            </div>
            <div className="p-[var(--spacing-3)] space-y-[var(--spacing-1)]">
              {attentionCards.length === 0 ? (
                <p className="text-[14px] text-[var(--color-text-muted)] py-[var(--spacing-4)] text-center flex items-center justify-center gap-[var(--spacing-2)]">
                  All clear
                  <HugeiconsIcon icon={CheckmarkCircle02Icon} size={16} className="text-[var(--color-state-success)]" />
                </p>
              ) : (
                attentionCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => onSelectCard(card.id)}
                    className={cn(
                      "w-full text-left flex items-center gap-[var(--spacing-2)] rounded-[var(--radius-md)] px-[var(--spacing-3)] py-[var(--spacing-2)]",
                      "hover:bg-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)] cursor-pointer",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
                    )}
                  >
                    <PriorityDot priority={card.priority} />
                    <span className="text-[14px] text-[var(--color-text-primary)] truncate flex-1">{card.title}</span>
                    <OwnerTag owner={card.owner} />
                    <StatusChip status={card.status} />
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Today's checklist — compact summary */}
          <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] animate-stagger-in" style={{ animationDelay: "250ms" }}>
            <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center justify-between">
              <div className="flex items-center gap-[var(--spacing-2)]">
                <NucleoIcon icon={<IconTasks size={16} />} className="text-[var(--color-accent-300)]" />
                <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Today&apos;s Checklist</span>
              </div>
              <span className="text-[14px] tabular-nums text-[var(--color-text-muted)]">{checklistProgress.checked}/{checklistProgress.total}</span>
            </div>
            <div className="p-[var(--spacing-3)] space-y-[var(--spacing-2)]">
              {sections
                .map((section) => {
                  const sectionItems = checklistItems.filter((i) => i.section === section.key);
                  if (sectionItems.length === 0) return null;
                  const doneCount = sectionItems.filter((i) => i.checked).length;
                  const pct = Math.round((doneCount / sectionItems.length) * 100);
                  return (
                    <div key={section.key} className="flex items-center gap-[var(--spacing-3)] px-[var(--spacing-1)]">
                      <span className="text-[14px] text-[var(--color-text-secondary)] w-[60px] shrink-0">{section.label.split(" — ")[0]}</span>
                      <div className="flex-1 h-[6px] rounded-full bg-[var(--color-border-stroke)] overflow-hidden">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all",
                            pct >= 100 ? "bg-[var(--color-state-success)]" : "bg-[var(--color-accent)]",
                          )}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-[14px] tabular-nums text-[var(--color-text-muted)] shrink-0">{doneCount}/{sectionItems.length}</span>
                    </div>
                  );
                })
                .filter(Boolean)}
              <div className="pt-[var(--spacing-1)] text-center">
                <button
                  type="button"
                  onClick={() => onNavigate?.("checklist")}
                  className={cn(
                    "text-[12px] text-[var(--color-text-muted)] cursor-pointer",
                    "hover:text-[var(--color-accent)] transition-colors duration-[120ms]",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] rounded-[var(--radius-sm)]",
                    "px-[var(--spacing-2)] py-[var(--spacing-1)]",
                  )}
                >
                  View full checklist → <kbd className="font-mono">9</kbd>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Board summary table */}
        <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] overflow-hidden animate-stagger-in" style={{ animationDelay: "300ms" }}>
          <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-2)]">
            <NucleoIcon icon={<IconSquareKanban size={16} />} className="text-[var(--color-text-muted)]" />
            <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Board Summary</span>
          </div>
          <table className="w-full text-[14px]">
            <thead>
              <tr className="border-b border-[var(--color-border-stroke)]">
                <th className="text-left font-semibold text-[var(--color-text-muted)] px-[var(--spacing-4)] py-[var(--spacing-2)]">Lane</th>
                <th className="text-right font-semibold text-[var(--color-text-muted)] px-[var(--spacing-3)] py-[var(--spacing-2)] w-[60px]">One-off</th>
                <th className="text-right font-semibold text-[var(--color-text-muted)] px-[var(--spacing-3)] py-[var(--spacing-2)] w-[60px]">Daily</th>
                <th className="text-right font-semibold text-[var(--color-text-muted)] px-[var(--spacing-3)] py-[var(--spacing-2)] w-[60px]">Weekly</th>
                <th className="text-right font-semibold text-[var(--color-text-muted)] px-[var(--spacing-3)] py-[var(--spacing-2)] w-[70px]">Planned</th>
                <th className="text-right font-semibold text-[var(--color-text-muted)] px-[var(--spacing-3)] py-[var(--spacing-2)] w-[70px]">Active</th>
                <th className="text-right font-semibold text-[var(--color-text-muted)] px-[var(--spacing-3)] py-[var(--spacing-2)] w-[70px]">Blocked</th>
                <th className="text-right font-semibold text-[var(--color-text-muted)] px-[var(--spacing-3)] py-[var(--spacing-2)] w-[60px]">Done</th>
              </tr>
            </thead>
            <tbody>
              {laneBreakdown.map((lane) => (
                <tr key={lane.id} className="border-b border-[var(--color-border-stroke)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors">
                  <td className="px-[var(--spacing-4)] py-[var(--spacing-2)] text-[var(--color-text-primary)]">{lane.label}</td>
                  <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums text-[var(--color-text-secondary)]">{lane.oneOffCount}</td>
                  <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums text-[var(--color-text-secondary)]">{lane.dailyCount}</td>
                  <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums text-[var(--color-text-secondary)]">{lane.weeklyCount}</td>
                  <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums text-[var(--color-text-muted)]">{lane.planned}</td>
                  <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums text-[var(--color-accent-300)]">{lane.active}</td>
                  <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums text-[var(--color-state-error)]">{lane.blocked}</td>
                  <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums text-[var(--color-state-success)]">{lane.done}</td>
                </tr>
              ))}
              <tr className="bg-[var(--color-bg-hover)]">
                <td className="px-[var(--spacing-4)] py-[var(--spacing-2)] font-semibold text-[var(--color-text-primary)]">Total</td>
                <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums font-semibold text-[var(--color-text-secondary)]">{laneBreakdown.reduce((s, l) => s + l.oneOffCount, 0)}</td>
                <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums font-semibold text-[var(--color-text-secondary)]">{laneBreakdown.reduce((s, l) => s + l.dailyCount, 0)}</td>
                <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums font-semibold text-[var(--color-text-secondary)]">{laneBreakdown.reduce((s, l) => s + l.weeklyCount, 0)}</td>
                <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums font-semibold text-[var(--color-text-muted)]">{statusCounts.planned}</td>
                <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums font-semibold text-[var(--color-accent-300)]">{statusCounts.in_progress}</td>
                <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums font-semibold text-[var(--color-state-error)]">{statusCounts.blocked}</td>
                <td className="px-[var(--spacing-3)] py-[var(--spacing-2)] text-right tabular-nums font-semibold text-[var(--color-state-success)]">{statusCounts.done}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
